import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { CartProvider } from '@/context/CartContext';
import { WishlistProvider } from '@/context/WishlistContext';
import { ActivityProvider } from '@/context/ActivityContext';
import Layout from '@/components/Layout/Layout';
import AdminLayout from '@/pages/admin/AdminLayout';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Pages
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ProductsPage from '@/pages/ProductsPage';
import ProductDetailPage from '@/pages/ProductDetailPage';
import CartPage from '@/pages/CartPage';
import CheckoutPage from '@/pages/CheckoutPage';
import OrdersPage from '@/pages/OrdersPage';
import OrderDetailPage from '@/pages/OrderDetailPage';
import DashboardPage from '@/pages/DashboardPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentPage from '@/pages/PaymentPage';
import QuickOrderPage from '@/pages/QuickOrderPage';
import WishlistPage from '@/pages/WishlistPage';
import PreviewPage from '@/pages/PreviewPage';
import CreditApplicationPage from '@/pages/CreditApplicationPage';

// Admin Pages
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminProducts from '@/pages/admin/AdminProducts';
import AdminOrders from '@/pages/admin/AdminOrders';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminInventory from '@/pages/admin/AdminInventory';
import AdminPricing from '@/pages/admin/AdminPricing';
import AdminProductEdit from '@/pages/admin/AdminProductEdit';
import AdminCategories from '@/pages/admin/AdminCategories';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminBanners from '@/pages/admin/AdminBanners';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';
import AdminActivity from '@/pages/admin/AdminActivity';
import AdminOutOfStock from '@/pages/admin/AdminOutOfStock';
import AdminCreditApplications from '@/pages/admin/AdminCreditApplications';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#64748B]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#64748B]">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={isAdmin ? '/admin' : '/products'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - Only login/register */}
      <Route path="/" element={<ProtectedRoute><Layout><HomePage /></Layout></ProtectedRoute>} />
      <Route path="/login" element={<PublicRoute><Layout><LoginPage /></Layout></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Layout><RegisterPage /></Layout></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><Layout><ForgotPasswordPage /></Layout></PublicRoute>} />
      <Route path="/preview/:token" element={<PreviewPage />} />
      <Route path="/products" element={<ProtectedRoute><Layout><ProductsPage /></Layout></ProtectedRoute>} />
      <Route path="/products/:id" element={<ProtectedRoute><Layout><ProductDetailPage /></Layout></ProtectedRoute>} />

      {/* Protected Routes - Buyers */}
      <Route path="/cart" element={<ProtectedRoute><Layout><CartPage /></Layout></ProtectedRoute>} />
      <Route path="/checkout" element={<ProtectedRoute><Layout><CheckoutPage /></Layout></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><Layout><OrdersPage /></Layout></ProtectedRoute>} />
      <Route path="/orders/:id" element={<ProtectedRoute><Layout><OrderDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/payment" element={<ProtectedRoute><Layout><PaymentPage /></Layout></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><Layout><PaymentSuccessPage /></Layout></ProtectedRoute>} />
      <Route path="/quick-order" element={<ProtectedRoute><Layout><QuickOrderPage /></Layout></ProtectedRoute>} />
      <Route path="/wishlist" element={<ProtectedRoute><Layout><WishlistPage /></Layout></ProtectedRoute>} />
      <Route path="/credit-application" element={<ProtectedRoute><Layout><CreditApplicationPage /></Layout></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><AdminLayout><AdminAnalytics /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/activity" element={<ProtectedRoute adminOnly><AdminLayout><AdminActivity /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute adminOnly><AdminLayout><AdminProducts /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/products/:id" element={<ProtectedRoute adminOnly><AdminLayout><AdminProductEdit /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/out-of-stock" element={<ProtectedRoute adminOnly><AdminLayout><AdminOutOfStock /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/inventory" element={<ProtectedRoute adminOnly><AdminLayout><AdminInventory /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/pricing" element={<ProtectedRoute adminOnly><AdminLayout><AdminPricing /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/categories" element={<ProtectedRoute adminOnly><AdminLayout><AdminCategories /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/banners" element={<ProtectedRoute adminOnly><AdminLayout><AdminBanners /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute adminOnly><AdminLayout><AdminOrders /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminLayout><AdminUsers /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminLayout><AdminSettings /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/credit-applications" element={<ProtectedRoute adminOnly><AdminLayout><AdminCreditApplications /></AdminLayout></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <ActivityProvider>
          <CartProvider>
            <WishlistProvider>
              <AppRoutes />
            </WishlistProvider>
          </CartProvider>
        </ActivityProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
